import type {
  GamePhase,
  PlayerColor,
  Row5,
  YinshState,
} from "@cloudnest/yinsh-engine";

/** Everything `resolveBoardTap` needs, pulled from game + UI state. */
export interface BoardTapContext {
  readonly phase: GamePhase;
  readonly currentPlayer: PlayerColor;
  readonly board: YinshState["board"];
  readonly selectedRingPosition: string | null;
  readonly validDestinations: readonly string[];
  readonly pendingRows: readonly Row5[];
}

/** The intent a board tap resolves to. The caller maps these to store actions. */
export type BoardTapAction =
  | { readonly kind: "ignore" }
  | { readonly kind: "placeRing"; readonly position: string }
  | { readonly kind: "selectRing"; readonly position: string }
  | { readonly kind: "move"; readonly from: string; readonly to: string }
  | { readonly kind: "removeRing"; readonly position: string }
  | { readonly kind: "rowCandidate"; readonly position: string };

/**
 * Decide what a tap on `position` means, given the current game + selection
 * state. Pure and side-effect free so the rules are unit-testable without a
 * DOM or store.
 *
 * Selection is **tap-to-lock** (design decision 2026-05-22): once a ring is
 * selected it stays selected until the player taps a valid destination, taps
 * another of their own rings to switch, or cancels (pill / Esc — handled by
 * the caller). A stray tap on an empty cell or an opponent's piece is a no-op
 * — it must never silently clear the selection.
 */
export function resolveBoardTap(
  ctx: BoardTapContext,
  position: string,
): BoardTapAction {
  const {
    phase,
    currentPlayer,
    board,
    selectedRingPosition,
    validDestinations,
    pendingRows,
  } = ctx;
  const cell = board[position] ?? null;
  const isOwnRing = cell?.type === "RING" && cell.player === currentPlayer;

  switch (phase) {
    case "RING_PLACEMENT":
      // Rings go only on empty intersections; the engine validator is the
      // final authority, this just avoids a no-op round-trip on occupied cells.
      return cell === null
        ? { kind: "placeRing", position }
        : { kind: "ignore" };

    case "PLAYING":
      if (selectedRingPosition !== null) {
        if (validDestinations.includes(position)) {
          return { kind: "move", from: selectedRingPosition, to: position };
        }
        if (isOwnRing) {
          return { kind: "selectRing", position };
        }
        return { kind: "ignore" };
      }
      return isOwnRing ? { kind: "selectRing", position } : { kind: "ignore" };

    case "REMOVING_ROW":
      if (pendingRows.length > 0) {
        const isCandidate = pendingRows.some((r) =>
          r.positions.includes(position),
        );
        return isCandidate
          ? { kind: "rowCandidate", position }
          : { kind: "ignore" };
      }
      return isOwnRing ? { kind: "removeRing", position } : { kind: "ignore" };

    case "GAME_END":
      return { kind: "ignore" };
  }
}
